import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { LogOut, Home, Crown, LineChart } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import type { User } from '@/types'
import { D12Icon } from '@/components/icons/D12Icon'
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner'

interface NavigationProps {
  user: User
  onLogout: () => void
}

// Navbar das rotas protegidas (mobile-first, redesign 2026-07).
// No mobile os itens viram so icone; o avatar leva ao perfil.
export function Navigation({ user, onLogout }: NavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === '/pagina-inicial';

  const handleNavigateHome = () => {
    navigate('/pagina-inicial');
  };

  return (
    <>
    <ImpersonationBanner />
    <nav className="sticky top-0 z-50 border-b border-border bg-card">
      <div className="container mx-auto px-2 sm:px-4">
        <div className="flex h-14 items-center justify-between gap-1 sm:h-16">
          <div className="flex min-w-0 items-center gap-0.5 sm:gap-2">
            <Button
              variant="ghost"
              onClick={handleNavigateHome}
              className="flex shrink-0 items-center gap-1.5 px-2 sm:gap-2 sm:px-3"
            >
              <D12Icon size={30} className="text-primary" />
              <span className="text-base font-black tracking-tight sm:text-xl">
                EDURPG<span className="text-primary">.IA</span>
              </span>
            </Button>

            {!isHomePage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateHome}
                className="flex items-center gap-2 px-2 sm:px-3"
                title="Página inicial"
              >
                <Home className="h-4 w-4" />
                <span className="hidden md:inline">Página Inicial</span>
              </Button>
            )}

            {user.role === 'superadmin' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-2 text-admin hover:text-admin sm:px-3"
                title="Admin"
              >
                <Crown className="h-4 w-4" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            )}

            {(user.role === 'professor' || user.role === 'superadmin') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/engajamento')}
                className="flex items-center gap-2 px-2 sm:px-3"
                title="Engajamento"
              >
                <LineChart className="h-4 w-4" />
                <span className="hidden md:inline">Engajamento</span>
              </Button>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-3">
            <button
              onClick={() => navigate('/perfil')}
              className="flex items-center gap-3 rounded-full p-0.5 transition-colors hover:bg-section sm:rounded-lg sm:px-2 sm:py-1"
              title="Meu perfil"
            >
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                <AvatarFallback className="bg-primary text-sm text-primary-foreground">
                  {user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-left md:block">
                <span className="block text-sm font-medium">{user.full_name}</span>
                <Badge
                  variant={user.role === 'professor' || user.role === 'superadmin' ? 'default' : 'secondary'}
                  className={user.role === 'superadmin' ? 'bg-admin text-white' : ''}
                >
                  {user.role === 'superadmin' ? 'Admin' : user.role === 'professor' ? 'Professor' : 'Estudante'}
                </Badge>
              </span>
            </button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="flex items-center gap-2 px-2 sm:px-3"
              title="Sair"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden md:inline">Sair</span>
            </Button>
          </div>
        </div>
      </div>
    </nav>
    </>
  )
}
